import { Button, ConfigProvider, DatePicker, Form, Input, InputNumber, message, Modal, Select } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { TransactionModel } from "@/core/models/transaction.model";
import { TransactionRepo } from "@/core/repos/tables/transaction.repo";
import { transactionExtraTypePipe } from "@/core/pipes/transaction-extra-type.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { ContractSelect } from "@/app/home/components/ContractSelect";
import { CopyOutlined, SyncOutlined } from "@ant-design/icons";
import { BillingPaymentType, TransactionExtraType, TransactionObjectType } from "@/core/enums/global.enums";
import { transactionObjectTypePipe } from "@/core/pipes/transaction-object-type.pipe";
import AuthRepo from "@/core/repos/auth.repo";
import { dateNoTZ, DEF_DATE_FORMAT, DEF_DATETIME_FORMAT, GlobalUtils } from "@/app/home/utils/global.utils";
import { useDrafts } from '@/core/stores/drafts.store';
import { ResetDraftButton } from '@/app/home/components/drafts/ResetDraftButton';
import { billingPaymentTypePipe } from "@/core/pipes/billing-payment-type.pipe";
import { currencyFormatter } from "@/core/pipes/format-currency.pipe";
import { Dayjs } from "dayjs";

const transactionRepo = container.get(TransactionRepo);
const authRepo = container.get(AuthRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: TransactionModel,
}

const INPUT_WIDTH = 450;

const ALLOWED_PAYMENT_TYPES = [
    BillingPaymentType.Cash,
    BillingPaymentType.Transfer
]

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const userDetail = authRepo.useDetails();
    const claims = authRepo.useClaims();
    const [form] = Form.useForm<TransactionModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [transactionTypes, setTransactionTypes] = useState<TransactionExtraType[]>([]);
    const [transactionObjectTypes, setTransactionObjectTypes] = useState<string[]>([]);
    const [permissionType, setPermissionType] = useState<string>();
    const [currentObjectType, setCurrentObjectType] = useState<string>();
    // Draft logic for TransactionModel
    const draft = useDrafts((state) => state.getDraft('TransactionModel'));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => _saveDraft('TransactionModel', form.getFieldsValue());
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft('TransactionModel');
        form.setFieldsValue(data);
    };
    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        form.setFieldsValue(data);
        if (data.id && data.id.length > 0) {
            setIsEdit(true);
        } else {
            if (draft) {
                form.setFieldsValue(draft);
                const objectType = form.getFieldValue("objectType");
                if (objectType) {
                    onObjectTypeChange(objectType, false);
                    setCurrentObjectType(objectType);
                }
            };
            setIsEdit(false);
        }
    }

    useEffect(() => {
        if (userDetail?.userName.toLowerCase() === "admin") {
            setPermissionType("T.KT");
            setTransactionObjectTypes([TransactionObjectType.AppUser, TransactionObjectType.WaterUser]);
        } else if (GlobalUtils.checkPerm(claims, "T.KT")) {
            setPermissionType("T.KT");
            setTransactionObjectTypes([TransactionObjectType.AppUser, TransactionObjectType.WaterUser]);
        } else if (GlobalUtils.checkPerm(claims, "T.A")) {
            setPermissionType("T.A");
            setTransactionObjectTypes([TransactionObjectType.WaterUser]);
        } 
    }, [claims])

    useEffect(() => {
        handleDataChange();
    }, [data])

    // Initialize select options data
    const init = async () => {
        // await populateTollAreas();
    }
    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    const onObjectTypeChange = (value: string, reset: boolean = true) => {
        if (reset) form.setFieldsValue({extraType: undefined, beneficiaryName: undefined});
        setCurrentObjectType(value);
        if (value === "WaterUser") {
            if (permissionType === "T.A") {
                setTransactionTypes([
                    TransactionExtraType.Deposit,
                ]);
            } else if (permissionType === "T.KT") {
                setTransactionTypes([
                    TransactionExtraType.Deposit,
                    TransactionExtraType.Refund,
                    TransactionExtraType.EnvironmentFee,
                ]);
            }
        } else if (value === "AppUser") {
            setTransactionTypes([
                TransactionExtraType.Refund,
                TransactionExtraType.EnvironmentFee,
                TransactionExtraType.EmployeeDebt,
            ]);
        }
    }

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (isEdit) {
                formData.id = data.id;
                const result = await transactionRepo.update({id: data.id, actualReceivedDate: dateNoTZ(formData.actualReceivedDate)});
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Đã cập nhật yêu cầu"
                    })
                } else {
                    messageApi.error({
                        content: "Cập nhật yêu cầu thất bại"
                    })
                }
            } else {
                const result = await transactionRepo.create({...data, ...formData, actualReceivedDate: dateNoTZ(formData.actualReceivedDate)});
                setIsModalOpen(false);
                clearDraft();
                if (result) {
                    messageApi.success({
                        content: "Thêm yêu cầu mới thành công"
                    })
                } else {
                    messageApi.error({
                        content: "Thêm yêu cầu mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
      if (!isEdit) saveDraft();
      setIsModalOpen(false);
    };

    const handleCopy = (value: string) => {
        navigator.clipboard.writeText(value).then(() => {
            message.success('Đã sao chép vào bộ nhớ tạm!');
        }).catch(() => {
            message.error('Sao chép thất bại!');
        });
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal open={isModalOpen} onCancel={handleCancel} width={1000}
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">THÔNG TIN CHI TIẾT</p>
                    {!isEdit && <ResetDraftButton onClick={clearDraft} />}
                </div>
                <div className="flex flex-row justify-center mt-6">
                    {isEdit?<Form<TransactionModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={!permissionStore.page.edit}
                    >
                        <div className="flex">
                            <div className="flex flex-col w-1/2 pr-4">
                                <Form.Item<TransactionModel>
                                    label="Người thụ hưởng"
                                    name="beneficiaryName"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Đối tượng giao dịch"
                                    name="objectType"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Select placeholder="Chọn đối tượng" style={{width: INPUT_WIDTH}} disabled>
                                        {Object.keys(TransactionObjectType).map(type => <Select.Option key={type} value={type}>{transactionObjectTypePipe(type)}</Select.Option>)}
                                    </Select>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Loại giao dịch"
                                    name="extraType"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Select placeholder="Chọn loại giao dịch" style={{width: INPUT_WIDTH}} disabled>
                                        {Object.keys(TransactionExtraType).map(type => <Select.Option key={type} value={type}>{transactionExtraTypePipe(type)}</Select.Option>)}
                                    </Select>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Thông tin giao dịch"
                                    name="information"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Input.TextArea style={{width: INPUT_WIDTH+18, height: 378, resize: "none", marginRight: 0}} disabled></Input.TextArea>
                                </Form.Item>
                            </div>
                            <div className="flex flex-col w-1/2">
                                <Form.Item<TransactionModel>
                                    label="Mã tham chiếu"
                                    name="id"
                                >
                                    <Input 
                                    addonAfter={
                                        <Button type="text" style={{height: 30}} icon={<CopyOutlined />} onClick={() => handleCopy(form.getFieldValue("id"))}></Button>
                                    }
                                    style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
  
                                <Form.Item<TransactionModel>
                                    label="Số tiền giao dịch"
                                    name="amount"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Phương thức thanh toán"
                                    name="paymentMethod"
                                >
                                    <Select placeholder="Chọn phương thức thanh toán" style={{width: INPUT_WIDTH}} disabled>
                                        {ALLOWED_PAYMENT_TYPES.map(type => <Select.Option key={type} value={type}>{billingPaymentTypePipe(type)}</Select.Option>)}
                                    </Select>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Số dư hiện tại"
                                    name="currentBalance"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Ngày nhận tiền thực tế"
                                    name="actualReceivedDate"
                                    getValueProps={(i)=>({value: dayjsPipe(i)})}
                                >
                                    <DatePicker format={DEF_DATETIME_FORMAT} showTime style={{width: INPUT_WIDTH}}></DatePicker>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Thời gian giao dịch"
                                    name="createdAt"
                                    getValueProps={(i)=>({value: dayjsPipe(i)})}
                                >
                                    <DatePicker format={DEF_DATE_FORMAT} disabled style={{width: INPUT_WIDTH}}></DatePicker>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Người tạo"
                                    name="createdBy"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Mã Liên kết"
                                    name="extraID"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>
                            </div>
                        </div>
                    </Form>: <Form<TransactionModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                    >

                        <div className="flex">
                            <div className="flex flex-col w-1/2 pr-4">
                                <Form.Item<TransactionModel>
                                    label="Đối tượng giao dịch"
                                    name="objectType"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Select placeholder="Chọn đối tượng" style={{width: INPUT_WIDTH}} onChange={(t) => onObjectTypeChange(t)}>
                                        {transactionObjectTypes.map(type => <Select.Option key={type} value={type}>{transactionObjectTypePipe(type)}</Select.Option>)}
                                    </Select>
                                </Form.Item>
                                <Form.Item<TransactionModel>
                                    label="Thông tin giao dịch"
                                    name="information"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Input.TextArea style={{width: INPUT_WIDTH+18, height: 378, resize: "none", marginRight: 0}}></Input.TextArea>
                                </Form.Item>
                            </div>
                            <div className="flex flex-col w-1/2">
                                <Form.Item<TransactionModel>
                                    label="Người thụ hưởng"
                                    name="beneficiaryName"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <ContractSelect 
                                        searchType={currentObjectType === 'WaterUser' ? 'WaterUser' : 'AppUser'}
                                        className="w-full"
                                        disabled={currentObjectType === undefined}
                                        style={{width: INPUT_WIDTH}}
                                        placeholder={currentObjectType === 'WaterUser' ? 'Chọn khách hàng' : 'Chọn người dùng'}
                                        onChange={(value) => {
                                            form.setFieldsValue({beneficiaryName: value});
                                        }}
                                    />
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Loại giao dịch"
                                    name="extraType"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Select placeholder="Chọn loại giao dịch" style={{width: INPUT_WIDTH}}>
                                        {transactionTypes.map(type => <Select.Option key={type} value={type}>{transactionExtraTypePipe(type)}</Select.Option>)}
                                    </Select>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Phương thức thanh toán"
                                    name="paymentMethod"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Select placeholder="Chọn phương thức thanh toán" style={{width: INPUT_WIDTH}}>
                                        {ALLOWED_PAYMENT_TYPES.map(type => <Select.Option key={type} value={type}>{billingPaymentTypePipe(type)}</Select.Option>)}
                                    </Select>
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Số tiền giao dịch"
                                    name="amount"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <InputNumber controls={false} formatter={currencyFormatter} style={{width: INPUT_WIDTH}} />
                                </Form.Item>

                                <Form.Item<TransactionModel>
                                    label="Ngày nhận tiền thực tế"
                                    name="actualReceivedDate"
                                    getValueProps={(i)=>({value: dayjsPipe(i)})}
                                >
                                    <DatePicker format={DEF_DATETIME_FORMAT} showTime style={{width: INPUT_WIDTH}}></DatePicker>
                                </Form.Item>

                                 <Form.Item<TransactionModel>
                                    label="Mã Liên kết"
                                    name="extraID"
                                >
                                    <Input 
                                    addonAfter={
                                        <Button type="text" style={{height: 30}} icon={<CopyOutlined />} onClick={() => handleCopy(form.getFieldValue("extraID"))}></Button>
                                    }
                                    style={{width: INPUT_WIDTH}}></Input>
                                </Form.Item>
                            </div>
                        </div>
                    </Form>}
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}